import { create } from "zustand";
import { immer } from "zustand/middleware/immer";
import { persist, devtools } from "zustand/middleware";
import { temporal } from "zundo";
import { z } from "zod";
import {
  FormDetailResponseSchema,
  FormFieldEditSchema,
  UpdateFormRequest,
} from "@codway-dev/shared";
import { FormService } from "../services/form.service";

// --- State Interfaces ---

// Infer types directly from the shared contract
export type BackendFormConfig = z.infer<typeof FormDetailResponseSchema>;
export type BackendField = z.infer<typeof FormFieldEditSchema>;

// Extend for UI specific needs (e.g. tempId for drag and drop)
interface EditorUIProps {
  tempId: string;
  defaultValue?: string;
  style?: {
    width?: "50%" | "100%";
    fontFamily?: string;
    borderColor?: string;
    backgroundColor?: string;
    cornerRadius?: number;
    color?: string;
  };
  internal_text?: string;
  placeholder?: string;
  show_icon?: boolean;
  border_color?: string;
  field_background_color?: string;
  error_message?: string;
}

export interface ComponentSettings {
  shipping_method?: {
    background_color?: string;
    text_color?: string;
    active_step_color?: string;
    show_radio_button?: boolean;
    active_border_color?: string;
  };
  order_summary?: {
    background_color?: string;
    subtotal_text?: string;
    shipping_text?: string;
    total_text?: string;
    show_tax_notice?: boolean;
    border_width?: number;
    border_radius?: number;
  };
  submit_button?: {
    text?: string;
    background_color?: string;
    text_color?: string;
    border_radius?: number;
  };
}

export interface DesignSettings {
  start_screen?: {
    logo?: string;
    title?: string;
    subtitle?: string;
    button_text?: string;
    show_background_image?: boolean;
    background_image?: string;
  };
  primary_color?: string;
  background_color?: string;
  corner_radius?: number;
  font_family?: string;
  show_logo?: boolean;
  logo_url?: string;
  form_title?: string;
  template_slug?: string;
  layout_mode?: "embedded" | "popup";
  submit_button_text?: string;
  // Typography
  font_size?: number;
  text_color?: string;

  // Container
  border_color?: string;
  border_width?: number;
  shadow_intensity?: number;

  // Fields
  field_background_color?: string;
  field_text_color?: string;
  field_prefix_background_color?: string;

  components?: ComponentSettings;
  [key: string]: unknown;
}

// Structural components that might not be in BackendField yet or need explicit definition for Editor
type StructuralField = {
  type: "shipping_method" | "order_summary";
  key: string;
  label: string;
  visible: boolean;
  display_order: number;
  required?: boolean;
  locked?: boolean;
};

// Intersection distributes over union: (A | B) & C = (A & C) | (B & C)
// This preserves the discriminated union structure.
export type EditorField = (BackendField | StructuralField) & EditorUIProps;

// Config used internally by the editor
export type EditorFormConfig = Omit<BackendFormConfig, "fields" | "design"> & {
  fields: EditorField[];
  design: {
    template_id: string;
    settings: DesignSettings; // Use our strictly typed settings
  };
};

interface UIState {
  activePanel:
    | "design"
    | "fields"
    | "field-settings"
    | "component-style"
    | null;
  selectedFieldId: string | null;
  selectedComponentId: string | null;
  viewMode: "desktop" | "mobile";
  editorMode: "edit" | "preview";
  isSaving: boolean; // Indicates if save is in progress
  previewDeliveryMode: "home" | "pickup";
}

interface EditorState {
  config: EditorFormConfig;
  ui: UIState;
  initialConfig: EditorFormConfig | null; // For optimistic discard
}

// --- Actions Interface ---

interface EditorActions {
  loadForm: (config: BackendFormConfig) => void;
  setActivePanel: (panel: UIState["activePanel"]) => void;
  setSelectedField: (id: string | null) => void;
  setSelectedComponent: (id: string | null) => void;
  setViewMode: (mode: UIState["viewMode"]) => void;
  setEditorMode: (mode: UIState["editorMode"]) => void;
  setPreviewDeliveryMode: (mode: "home" | "pickup") => void;

  // Generic update for deep nested properties
  // Generic update for deep nested properties
  // Generic update for deep nested properties
  updateDesignSetting: (key: string, value: unknown) => void;
  setTemplateId: (templateId: string) => void;

  // Generic update for component styles
  // Generic update for component styles
  updateComponentStyle: (
    componentId: string,
    key: string,
    value: unknown
  ) => void;

  updateFormName: (name: string) => void;

  addField: (field: EditorField) => void;
  removeField: (id: string) => void;
  updateField: (id: string, updates: Partial<EditorField>) => void;
  resetAllFieldBorders: () => void;
  moveField: (oldIndex: number, newIndex: number) => void;
  insertField: (field: EditorField, index: number) => void;
  saveForm: () => Promise<void>;
  discardChanges: () => void;
  reset: () => void;
  setFields: (fields: EditorField[]) => void;
  setCountry: (countryCode: string) => void;
}

// --- Store Implementation ---

type EditorStore = EditorState & EditorActions;

const INITIAL_CONFIG: EditorFormConfig = {
  id: "",
  name: "Untitled Form",
  slug: "untitled-form",
  is_active: false,
  country_code: "US",
  shop_name: "My Shopify Store", // Default fallback
  settings: {},
  design: {
    template_id: "default",
    settings: {
      primary_color: "#008060", // Shopify Green
      corner_radius: 4,
      components: {},
    },
  },
  fields: [],
};

const INITIAL_UI: UIState = {
  activePanel: null,
  selectedFieldId: null,
  selectedComponentId: null,
  viewMode: "desktop",
  editorMode: "edit",
  isSaving: false,
  previewDeliveryMode: "home",
};

export const selectIsDirty = (state: EditorStore) => {
  if (!state.initialConfig) return false;
  return JSON.stringify(state.config) !== JSON.stringify(state.initialConfig);
};

export const useEditorStore = create<EditorStore>()(
  devtools(
    persist(
      temporal(
        immer((set, get) => ({
          config: INITIAL_CONFIG,
          ui: INITIAL_UI,
          initialConfig: null, // Initialize as null

          loadForm: (config) =>
            set((state) => {
              // Map backend config to editor config (add tempIds)
              const editorFields: EditorField[] = config.fields.map((f) => ({
                ...f,
                tempId: crypto.randomUUID(),
              }));

              state.config = {
                ...config,
                fields: editorFields,
              };
              // Save initial state for rollback
              state.initialConfig = JSON.parse(JSON.stringify(state.config));
            }),

          setActivePanel: (panel) =>
            set((state) => {
              // If checking the same panel, toggle it off (unless it's a settings panel which usually stays open when switching items)
              if (
                state.ui.activePanel === panel &&
                panel !== "field-settings" &&
                panel !== "component-style"
              ) {
                state.ui.activePanel = null;
              } else {
                state.ui.activePanel = panel;

                // If switching to a global panel (not item specific), clear item selections
                if (panel === "design" || panel === "fields") {
                  state.ui.selectedFieldId = null;
                  state.ui.selectedComponentId = null;
                }
              }
            }),

          setSelectedField: (id) =>
            set((state) => {
              state.ui.selectedFieldId = id;

              if (id) {
                const field = (state.config.fields as EditorField[]).find(
                  (f) => f.tempId === id
                );

                if (
                  field &&
                  (field.type === "order_summary" ||
                    field.type === "shipping_method")
                ) {
                  state.ui.selectedComponentId = field.type;
                  state.ui.activePanel = "component-style";
                } else {
                  state.ui.selectedComponentId = null;
                  state.ui.activePanel = "field-settings";
                }
              } else {
                if (
                  (state.ui.activePanel === "field-settings" ||
                    state.ui.activePanel === "component-style") &&
                  state.ui.selectedComponentId !== "submit_button"
                ) {
                  state.ui.activePanel = null;
                  state.ui.selectedComponentId = null;
                }
              }
            }),

          setSelectedComponent: (id) =>
            set((state) => {
              state.ui.selectedComponentId = id;
              if (id) {
                state.ui.activePanel = "component-style";
                state.ui.selectedFieldId = null;
              } else if (state.ui.activePanel === "component-style") {
                state.ui.activePanel = null;
              }
            }),

          setViewMode: (mode) =>
            set((state) => {
              state.ui.viewMode = mode;
            }),
          setEditorMode: (mode) =>
            set((state) => {
              // console.log("Setting Editor Mode:", mode);
              state.ui.editorMode = mode;
              if (mode === "preview") {
                state.ui.selectedFieldId = null;
                // We might want to keep the active panel open or close it. For now, let's close it to give full view.
                state.ui.activePanel = null;
                state.ui.selectedComponentId = null;
              }
            }),
          setPreviewDeliveryMode: (mode) =>
            set((state) => {
              state.ui.previewDeliveryMode = mode;
            }),

          // Updates design.settings.[key]
          updateDesignSetting: (key, value) =>
            set((state) => {
              if (!state.config.design.settings) {
                state.config.design.settings = {};
              }
              // Dynamic key access on settings with safe cast
              (state.config.design.settings as Record<string, unknown>)[key] =
                value;
            }),

          setTemplateId: (templateId) =>
            set((state) => {
              state.config.design.template_id = templateId;
            }),

          updateComponentStyle: (componentId, key, value) =>
            set((state) => {
              if (!state.config.design.settings) {
                state.config.design.settings = {};
              }
              if (!state.config.design.settings.components) {
                state.config.design.settings.components = {};
              }

              // Typed access to components
              const components = state.config.design.settings
                .components as Record<string, Record<string, unknown>>;

              if (!components[componentId]) {
                components[componentId] = {};
              }
              components[componentId][key] = value;
            }),

          updateFormName: (name) =>
            set((state) => {
              state.config.name = name;
            }),

          addField: (field) =>
            set((state) => {
              // console.log("Store: Adding field", field);
              // Need to strip tempId before saving to DB, but keep it in store
              // The type intersection handles this in TS, but runtime is just an object.
              state.config.fields.push(field);
            }),

          insertField: (field, index) =>
            set((state) => {
              if (index < 0) index = 0;
              if (index > state.config.fields.length)
                index = state.config.fields.length;

              state.config.fields.splice(index, 0, field);

              // Normalize display_order immediately
              (state.config.fields as EditorField[]).forEach((f, idx) => {
                f.display_order = idx;
              });
            }),

          removeField: (tempId) =>
            set((state) => {
              // We use tempId for local operations if possible, otherwise map to key/id?
              // Since fields from backend won't have tempId initially unless we map them on load.
              // For now assuming we filter by tempId which must exist on all items in store.
              // NOTE: loadForm needs to ensure tempId exists.
              state.config.fields = (
                state.config.fields as EditorField[]
              ).filter((f) => f.tempId !== tempId);

              // If the removed field was selected, deselect it and close panel
              if (state.ui.selectedFieldId === tempId) {
                state.ui.selectedFieldId = null;
                if (state.ui.activePanel === "field-settings") {
                  state.ui.activePanel = null;
                }
              }
            }),

          updateField: (tempId, updates) =>
            set((state) => {
              const field = (state.config.fields as EditorField[]).find(
                (f) => f.tempId === tempId
              );
              if (field) {
                Object.assign(field, updates);
                Object.assign(field, updates);
              }
            }),

          resetAllFieldBorders: () =>
            set((state) => {
              (state.config.fields as EditorField[]).forEach((f) => {
                delete f.border_color;
              });
            }),

          moveField: (oldIndex, newIndex) =>
            set((state) => {
              if (
                oldIndex < 0 ||
                oldIndex >= state.config.fields.length ||
                newIndex < 0 ||
                newIndex >= state.config.fields.length
              ) {
                return;
              }

              const fields = state.config.fields as EditorField[];
              const [movedItem] = fields.splice(oldIndex, 1);
              fields.splice(newIndex, 0, movedItem);

              // Normalize display_order
              fields.forEach((f, index) => {
                f.display_order = index;
              });
            }),

          saveForm: async () => {
            set((state) => {
              state.ui.isSaving = true;
            });

            try {
              const state = get();
              const formId = state.config.id;

              if (!formId) {
                throw new Error("Form ID missing");
              }

              // 4. Normalize & 1. Type Safe Mapping
              // We map fields to strictly comply with BackendField Zod Schema
              // and normalize display_order based on current array index.
              const cleanFields: BackendField[] = state.config.fields.map(
                (f, index) => {
                  // Ensure strictly typed object creation to match BackendField
                  // We destruct UI props to ensure they don't leak, but we construct explicitly.
                  const base = {
                    key: f.key,
                    label: f.label,
                    visible: f.visible,
                    display_order: index,
                    locked: f.locked,
                    type: f.type,
                    required: f.required,
                  };

                  // Add optional props only if they exist and are valid for the schema
                  const extraMatches: Record<string, unknown> = {};
                  if ("placeholder" in f)
                    extraMatches.placeholder = f.placeholder;
                  if ("options" in f) extraMatches.options = f.options;
                  if ("show_icon" in f && f.show_icon != null)
                    extraMatches.show_icon = f.show_icon;
                  if ("border_color" in f && f.border_color != null)
                    extraMatches.border_color = f.border_color;
                  if (
                    "field_background_color" in f &&
                    f.field_background_color != null
                  )
                    extraMatches.field_background_color =
                      f.field_background_color;
                  if ("offer_id" in f) extraMatches.offer_id = f.offer_id;
                  if ("display_type" in f)
                    extraMatches.display_type = f.display_type;

                  const cleanField = { ...base, ...extraMatches };

                  // 5. Validation Feedback
                  // Verify each field against schema individually to provide precise error
                  // We use safeParse to catch errors early before sending to backend
                  const result = FormFieldEditSchema.safeParse(cleanField);
                  if (!result.success) {
                    // Cast to ZodError to satisfy TS if needed, though safeParse returns ZodError
                    const error = result.error;
                    const errorMsg = error.issues
                      ? error.issues
                          .map((e) => `${e.path.join(".")}: ${e.message}`)
                          .join(", ")
                      : "Invalid data";
                    throw new Error(
                      `Validation Error in field '${f.label}': ${errorMsg}`
                    );
                  }

                  return result.data as BackendField;
                }
              );

              const updateData: UpdateFormRequest = {
                name: state.config.name,
                is_active: state.config.is_active,
                design: {
                  template_id: state.config.design.template_id,
                  settings: state.config.design.settings,
                },
                fields: cleanFields,
                settings: state.config.settings,
              };

              // API Call
              await FormService.saveFormConfig(formId, updateData);

              set((state) => {
                state.ui.isSaving = false;
                // Update initialConfig to match the new saved state for future discards
                state.initialConfig = JSON.parse(JSON.stringify(state.config));
              });
            } catch (error) {
              console.error("Failed to save form:", error);
              set((state) => {
                state.ui.isSaving = false;
              });

              // Ensure the error message is propagated cleanly
              throw error instanceof Error
                ? error
                : new Error("Unknown error during save");
            }
          },

          // 3. Optimistic Discard
          discardChanges: () =>
            set((state) => {
              if (state.initialConfig) {
                // Restore from memory instead of refetching
                state.config = JSON.parse(JSON.stringify(state.initialConfig));

                if (state.ui.selectedFieldId) {
                  const exists = state.config.fields.some(
                    (f) => f.tempId === state.ui.selectedFieldId
                  );
                  if (!exists) {
                    state.ui.selectedFieldId = null;
                    state.ui.activePanel = null;
                  }
                }
              } else {
                // Fallback if no initial config (shouldn't happen if loadForm was called)
                console.warn("No initialConfig found to discard to.");
              }
            }),

          reset: () =>
            set((state) => {
              state.config = INITIAL_CONFIG;
              state.config = INITIAL_CONFIG;
              state.ui = INITIAL_UI;
            }),

          setFields: (fields) =>
            set((state) => {
              // Preserve structural components (order_summary, shipping_method) that aren't in the new fields
              const STRUCTURAL_TYPES = ["order_summary", "shipping_method"];
              const incomingTypes = new Set(fields.map((f) => f.type));
              const preserved = (state.config.fields as EditorField[]).filter(
                (f) =>
                  STRUCTURAL_TYPES.includes(f.type) &&
                  !incomingTypes.has(f.type)
              );
              const merged = [...preserved, ...fields];
              merged.forEach((f, idx) => {
                f.display_order = idx;
              });
              state.config.fields = merged;
              state.ui.selectedFieldId = null;
              state.ui.selectedComponentId = null;
              state.ui.activePanel = null;
            }),

          setCountry: (countryCode) =>
            set((state) => {
              state.config.country_code = countryCode;
            }),
        })),
        {
          limit: 20, // Limit history size
          partialize: (state) => ({ config: state.config }), // Only track config changes
        }
      ),
      {
        name: "editor-storage",
        partialize: (state) => ({ config: state.config }),
      }
    ),
    { name: "EditorStore" }
  )
) as import("zustand").UseBoundStore<
  import("zustand").StoreApi<EditorStore>
> & {
  temporal: import("zustand").StoreApi<
    import("zundo").TemporalState<{ config: EditorFormConfig }>
  >;
};

// Expose store for Zukeeper
if (typeof window !== "undefined") {
  window.store = useEditorStore;
}

// Subscribe to changes to log them to console (for debugging in iframe)
// useEditorStore.subscribe((state) => {
//   console.log(" Editor State Updated:", state);
// });
